import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PasswordStrengthMeter, scorePassword } from "../../components/PasswordStrengthMeter";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { createAdmin, deleteAdmin, listAdmins, updateAdmin } from "../../lib/adminApi";

export function AdminAdminsPage() {
  const { admin: me } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "", is_superadmin: false });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPw, setEditPw] = useState("");

  async function load() {
    setLoading(true);
    try {
      setRows(await listAdmins());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load admins.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Live-computed strength for the create form. Kept as a memo so downstream JSX + the submit
  // handler both reference the same object without re-running the scorer.
  const createPwStrength = useMemo(() => scorePassword(form.password), [form.password]);
  const editPwStrength = useMemo(() => scorePassword(editPw), [editPw]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.email || !form.name || !form.password) {
      toast.error("Name, email and password are required.");
      return;
    }
    // Enforce a minimum strength of "Fair" (score >= 2) — refuses "password123", short passwords,
    // and single-character-class values that pass the backend's 6-char minimum but are still weak.
    if (createPwStrength.score < 2) {
      toast.error("Password is too weak. " + (createPwStrength.tips[0] || "Add length and mix character types."));
      return;
    }
    setBusy(true);
    try {
      const created = await createAdmin({
        email: form.email.trim(),
        name: form.name.trim(),
        password: form.password,
        is_superadmin: form.is_superadmin,
      });
      setRows((prev) => [...prev, created]);
      setForm({ email: "", name: "", password: "", is_superadmin: false });
      toast.success("Admin added.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add admin.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(a) {
    try {
      const updated = await updateAdmin(a.id, { is_active: !a.is_active });
      setRows((prev) => prev.map((r) => (r.id === a.id ? updated : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update.");
    }
  }

  async function togglePromote(a) {
    try {
      const updated = await updateAdmin(a.id, { is_superadmin: !a.is_superadmin });
      setRows((prev) => prev.map((r) => (r.id === a.id ? updated : r)));
      toast.success(updated.is_superadmin ? "Promoted to superadmin." : "Demoted to admin.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update.");
    }
  }

  async function saveEditPw(a) {
    if (editPw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (editPwStrength.score < 2) {
      toast.error("Password is too weak. " + (editPwStrength.tips[0] || "Add length and mix character types."));
      return;
    }
    try {
      await updateAdmin(a.id, { password: editPw });
      toast.success("Password updated.");
      setEditingId(null); setEditPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update.");
    }
  }

  async function remove(a) {
    if (!window.confirm(`Delete admin ${a.email}?`)) return;
    try {
      await deleteAdmin(a.id);
      setRows((prev) => prev.filter((r) => r.id !== a.id));
      toast.success("Admin deleted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete.");
    }
  }

  return (
    <div className="flex flex-col gap-8" data-testid="admin-admins-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold">Governance</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Admins</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Any admin can add other admins and manage members. Only a superadmin can promote another admin
          to superadmin. The founding superadmin ({me?.is_superadmin ? "you" : "parth@snehworld.com"}) cannot be deleted.
        </p>
      </div>

      <form onSubmit={handleCreate} className="rounded-lg border border-border bg-card p-6" data-testid="add-admin-form">
        <h3 className="text-lg font-semibold">Add a new admin</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="a-name">Name</Label>
            <Input id="a-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-admin-name" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="a-email">Email</Label>
            <Input id="a-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="a-pw">Password (min 6 chars)</Label>
            <Input id="a-pw" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-admin-password" />
            {form.password && <PasswordStrengthMeter password={form.password} testIdPrefix="new-admin-pw" />}
          </div>
          <label className="flex items-center gap-2 self-end pb-1 text-sm text-muted">
            <input
              type="checkbox"
              checked={form.is_superadmin}
              onChange={(e) => setForm({ ...form, is_superadmin: e.target.checked })}
              disabled={!me?.is_superadmin}
              data-testid="new-admin-superadmin"
              className="accent-gold"
            />
            Make this a superadmin
            {!me?.is_superadmin && <span className="text-xs">(only a superadmin can enable this)</span>}
          </label>
        </div>
        <div className="mt-4">
          <Button type="submit" disabled={busy} data-testid="submit-new-admin">
            {busy ? "Adding…" : "Add admin"}
          </Button>
        </div>
      </form>

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-lg font-semibold">All admins ({rows.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
              ) : (
                rows.map((a) => {
                  const isMe = a.id === me?.id;
                  const isSuper = a.is_superadmin;
                  return (
                    <tr key={a.id} className="border-b border-border/70 last:border-b-0 hover:bg-elevated/60" data-testid={`admin-row-${a.id}`}>
                      <td className="px-4 py-3 text-text">
                        {a.name} {isMe && <span className="ml-1 rounded bg-gold/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-gold">You</span>}
                      </td>
                      <td className="px-4 py-3 text-muted">{a.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                          isSuper ? "bg-gold/20 text-gold" : "bg-elevated text-muted"
                        }`}>
                          {isSuper ? "Superadmin" : "Admin"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                          a.is_active ? "bg-gold/10 text-gold" : "bg-muted/20 text-muted"
                        }`}>{a.is_active ? "Active" : "Disabled"}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3">
                        {editingId === a.id ? (
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex justify-end gap-2">
                              <Input
                                type="password"
                                value={editPw}
                                onChange={(e) => setEditPw(e.target.value)}
                                placeholder="New password"
                                className="w-40"
                                data-testid={`edit-pw-${a.id}`}
                              />
                              <Button size="sm" onClick={() => saveEditPw(a)} data-testid={`save-pw-${a.id}`}>Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditPw(""); }}>Cancel</Button>
                            </div>
                            {editPw && (
                              <div className="w-full max-w-xs">
                                <PasswordStrengthMeter password={editPw} testIdPrefix={`edit-pw-${a.id}`} />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            {me?.is_superadmin && !isSuper && (
                              <Button size="sm" variant="outline" onClick={() => togglePromote(a)} data-testid={`promote-${a.id}`}>
                                Promote
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setEditingId(a.id)} data-testid={`edit-pw-btn-${a.id}`}>
                              Reset PW
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleActive(a)}
                              disabled={isSuper || isMe}
                              data-testid={`admin-toggle-${a.id}`}
                            >
                              {a.is_active ? "Disable" : "Activate"}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => remove(a)}
                              disabled={isSuper || isMe}
                              data-testid={`admin-delete-${a.id}`}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
